import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "../slugs/vehiclePricing";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const sedanWithChauffeur: ChauffeurPage = {
  slug: "sedan-with-chauffeur",
  title: "Sedan with chauffeur — business travel",
  subtitle:
    "Mercedes E-Class with private chauffeur: the discreet business sedan across the French Riviera.",
  keywords: [
    "sedan with chauffeur Cannes",
    "Mercedes E-Class chauffeur",
    "business car service French Riviera",
    "private business chauffeur Nice",
  ],
  hero: {
    title: "Sedan with chauffeur on the French Riviera — Mercedes E-Class",
    subtitle:
      "The business sedan par excellence: discretion, punctuality and a comfortable workspace for your everyday professional travel.",
    keywords: ["sedan with chauffeur", "E-Class", "business car service"],
    imageSrc: heroImage.src,
    imageAlt: "Mercedes E-Class with private chauffeur in Cannes",
    badges: ["Up to 3 passengers", "Business discretion", "Punctuality"],
  },
  benefits: {
    heading: "Why the sedan with chauffeur from KS Rent Services",
    items: [
      {
        title: "Business discretion",
        description:
          "An understated, elegant sedan and a chauffeur in attire: the right image for your professional meetings and your clients.",
      },
      {
        title: "Punctuality",
        description:
          "Schedules kept, flight tracking and traffic anticipation: you arrive on time, stress-free, at every appointment.",
      },
      {
        title: "A comfortable workspace",
        description:
          "A quiet, comfortable cabin: take your calls, prepare your meetings and keep working while we drive.",
      },
      {
        title: "The right price",
        description:
          "The gateway to private chauffeur service: all the high-end service, at the most accessible rate in our fleet.",
      },
    ],
  },
  services: {
    heading: "Business sedan services",
    services: [
      {
        title: "Business meetings",
        text: "Professional travel around town, back-to-back meetings and inter-city trips across the French Riviera.",
      },
      {
        title: "Airport transfers",
        text: "Nice airport, stations and Monaco heliport, solo or duo, with a personal welcome and luggage handling.",
      },
      {
        title: "Hourly hire",
        text: "Chauffeur and sedan by the hour or day for your sales rounds and busy schedules.",
      },
      {
        title: "Corporate events",
        text: "Trade shows, conventions and corporate evenings: a dedicated chauffeur for you and your guests.",
      },
    ],
  },
  locations: {
    heading: "Areas served",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & airport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "Private chauffeur service at the service of your efficiency",
    subheading: "All the comfort of a high-end service, without the excess.",
    paragraphs: [
      "The Mercedes E-Class brings together the essence of private chauffeur service: a comfortable, discreet sedan, ideal for your daily business travel and transfers.",
      "You save precious time: while your chauffeur handles the route and parking, you stay focused on what matters.",
      "A single point of contact, transparent packages and the same punctuality standard as across our whole fleet.",
    ],
    bullets: [
      "Sedan for up to 3 passengers",
      "Transparent airport packages",
      "Available 24/7",
    ],
  },
  process: {
    heading: "How to book your sedan with chauffeur",
    steps: [
      "Tell us your route, your schedule and any stops.",
      "Receive a clear package or a per-kilometre estimate.",
      "Assignment of the E-Class and your dedicated chauffeur.",
      "Punctual, discreet and comfortable pick-up.",
    ],
  },
  faq: [
    {
      q: "Which vehicle is the sedan with chauffeur?",
      a: "A Mercedes E-Class, a comfortable business sedan for up to 3 passengers, ideal for professional travel and transfers.",
    },
    {
      q: "What is the difference with the S-Class?",
      a: "The E-Class is our business sedan at the best service-to-price ratio; the S-Class is the prestige sedan, for a VIP experience and demanding clients.",
    },
    {
      q: "Do you handle airport transfers?",
      a: "Yes, to and from Nice airport in particular, with transparent packages and the chauffeur's personal welcome.",
    },
    vehiclePriceFaq("en", "CLASS_E", "a sedan with chauffeur"),
    {
      q: "How do I book?",
      a: "Tell us your route and schedule: we quickly confirm the sedan and your dedicated chauffeur.",
    },
  ],
  relatedLinks: [
    {
      label: "Mercedes S-Class with chauffeur",
      href: "/en/chauffeur/s-class-with-chauffeur",
    },
    {
      label: "Van with chauffeur (7 seats)",
      href: "/en/chauffeur/van-with-chauffeur",
    },
    { label: "All our chauffeur services", href: "/en/chauffeur" },
    {
      label: "Wedding motorcade in Cannes",
      href: "/en/chauffeur/wedding-car-cannes",
    },
  ],
  cta: {
    title: "A business trip to organise?",
    text: "Tell us your route — fast quote for your sedan with chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Request a quote",
  },
  seo: {
    ...vehicleSeo(
      "en",
      "CLASS_E",
      "Sedan with chauffeur (E-Class), French Riviera",
      "Mercedes E-Class sedan with chauffeur",
    ),
    canonical: "/en/chauffeur/sedan-with-chauffeur",
  },
};

export default sedanWithChauffeur;
